// @figma file=tbd node=tbd name="Chart" set="tbd" verified=tbd
'use client';

import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { cn } from '../../lib/utils';

// ─── Chart Config ─────────────────────────────────────────────────────────────

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<'light' | 'dark', string> }
  );
};

// ─── Context ──────────────────────────────────────────────────────────────────

type ChartContextValue = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextValue | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) throw new Error('useChart must be used within a ChartContainer');
  return context;
}

// ─── ChartContainer ───────────────────────────────────────────────────────────

export interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config: ChartConfig;
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
}

const ChartContainer = React.forwardRef<HTMLDivElement, ChartContainerProps>(
  ({ id, className, children, config, ...props }, ref) => {
    const uniqueId = React.useId();
    const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`;

    return (
      <ChartContext.Provider value={{ config }}>
        <div
          data-chart={chartId}
          ref={ref}
          className={cn(
            'flex aspect-video justify-center text-xs',
            '[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground',
            "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50",
            '[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border',
            "[&_.recharts-dot[stroke='#fff']]:stroke-transparent",
            '[&_.recharts-layer]:outline-none',
            "[&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border",
            '[&_.recharts-radial-bar-background-sector]:fill-muted',
            '[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted',
            "[&_.recharts-reference-line_[stroke='#ccc']]:stroke-border",
            "[&_.recharts-sector[stroke='#fff']]:stroke-transparent",
            '[&_.recharts-sector]:outline-none',
            '[&_.recharts-surface]:outline-none',
            className,
          )}
          {...props}
        >
          <ChartStyle id={chartId} config={config} />
          <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
        </div>
      </ChartContext.Provider>
    );
  },
);
ChartContainer.displayName = 'ChartContainer';

// ─── ChartStyle ───────────────────────────────────────────────────────────────

function ChartStyle({ id, config }: { id: string; config: ChartConfig }) {
  const colorConfig = Object.entries(config).filter(([, v]) => v.theme || v.color);
  if (!colorConfig.length) return null;

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries({ light: '', dark: '' })
          .map(([theme]) =>
            colorConfig
              .map(([key, itemConfig]) => {
                const color =
                  itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ?? itemConfig.color;
                return color ? `  --color-${key}: ${color};` : null;
              })
              .filter(Boolean)
              .join('\n')
              ? `${theme === 'light' ? '' : '.dark '}[data-chart=${id}] {\n${colorConfig
                  .map(([key, itemConfig]) => {
                    const color =
                      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ??
                      itemConfig.color;
                    return color ? `  --color-${key}: ${color};` : null;
                  })
                  .filter(Boolean)
                  .join('\n')}\n}`
              : null,
          )
          .filter(Boolean)
          .join('\n'),
      }}
    />
  );
}

// ─── ChartTooltip ─────────────────────────────────────────────────────────────

const ChartTooltip = RechartsPrimitive.Tooltip;

export interface ChartTooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  payload?: RechartsPrimitive.TooltipPayload;
  label?: string | number;
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  nameKey?: string;
  labelKey?: string;
  labelFormatter?: (
    value: React.ReactNode,
    payload: RechartsPrimitive.TooltipPayload,
  ) => React.ReactNode;
}

const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelKey,
      nameKey,
    },
    ref,
  ) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) return null;
      const [item] = payload;
      const key = `${labelKey ?? item?.dataKey ?? item?.name ?? 'value'}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        !labelKey && typeof label === 'string'
          ? (config[label]?.label ?? label)
          : itemConfig?.label;

      if (labelFormatter) {
        return <div className="font-medium">{labelFormatter(value ?? label, payload)}</div>;
      }
      return value ? <div className="font-medium">{value}</div> : null;
    }, [hideLabel, payload, labelKey, label, config, labelFormatter]);

    if (!active || !payload?.length) return null;

    const nestLabel = payload.length === 1 && indicator !== 'dot';

    return (
      <div
        ref={ref}
        className={cn(
          'grid min-w-32 items-start gap-1.5 rounded-[var(--radius)] border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl',
          className,
        )}
      >
        {!nestLabel ? tooltipLabel : null}
        <div className="grid gap-1.5">
          {payload.map((item, _index) => {
            const key = `${nameKey ?? item.name ?? item.dataKey ?? 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = item.payload?.fill ?? item.color;

            return (
              <div
                key={`${item.dataKey ?? _index}`}
                className={cn(
                  'flex w-full flex-wrap items-stretch gap-2',
                  indicator === 'dot' && 'items-center',
                )}
              >
                {!hideIndicator && (
                  <div
                    className={cn(
                      'shrink-0 rounded-sm border-[--color-border] bg-[--color-bg]',
                      indicator === 'dot' && 'h-2.5 w-2.5 rounded-full',
                      indicator === 'line' && 'w-1',
                      indicator === 'dashed' && 'w-0 border border-dashed bg-transparent',
                      nestLabel && indicator === 'dashed' && 'my-0.5',
                    )}
                    // CSS custom properties required for dynamic recharts chart colors —
                    // not a hardcoded color violation; values come from chart config at runtime.
                    style={
                      {
                        '--color-bg': indicatorColor,
                        '--color-border': indicatorColor,
                      } as React.CSSProperties
                    }
                  />
                )}
                <div
                  className={cn(
                    'flex flex-1 justify-between leading-none',
                    nestLabel ? 'items-end' : 'items-center',
                  )}
                >
                  <div className="grid gap-1.5">
                    {nestLabel ? tooltipLabel : null}
                    <span className="text-muted-foreground">{itemConfig?.label ?? item.name}</span>
                  </div>
                  {item.value !== undefined && (
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {item.value.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltipContent';

// ─── ChartLegend ──────────────────────────────────────────────────────────────

const ChartLegend = RechartsPrimitive.Legend;

export interface ChartLegendContentProps extends React.HTMLAttributes<HTMLDivElement> {
  payload?: ReadonlyArray<RechartsPrimitive.LegendPayload>;
  verticalAlign?: 'top' | 'bottom' | 'middle';
  hideIcon?: boolean;
  nameKey?: string;
}

const ChartLegendContent = React.forwardRef<HTMLDivElement, ChartLegendContentProps>(
  ({ className, hideIcon = false, payload, verticalAlign = 'bottom', nameKey }, ref) => {
    const { config } = useChart();
    if (!payload?.length) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center gap-4',
          verticalAlign === 'top' ? 'pb-3' : 'pt-3',
          className,
        )}
      >
        {payload.map((item) => {
          const key = `${nameKey ?? item.dataKey ?? 'value'}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            <div key={item.value} className="flex items-center gap-1.5">
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                // backgroundColor: dynamic recharts chart color set at runtime — not a hardcoded value
                <div
                  className="h-2 w-2 shrink-0 rounded-sm"
                  style={{ backgroundColor: item.color }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
      </div>
    );
  },
);
ChartLegendContent.displayName = 'ChartLegendContent';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
): ChartConfig[string] | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const payloadRecord = payload as Record<string, unknown>;

  const payloadPayload =
    'payload' in payloadRecord &&
    typeof payloadRecord.payload === 'object' &&
    payloadRecord.payload !== null
      ? (payloadRecord.payload as Record<string, unknown>)
      : undefined;

  let configLabelKey: string = key;
  if (key in config) {
    configLabelKey = key;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key] === 'string' &&
    payloadPayload[key] in config
  ) {
    configLabelKey = payloadPayload[key] as string;
  }
  return config[configLabelKey];
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  useChart,
};
